import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { devicesApi, ApiError } from '../api/client';
import { useAuthStore } from '../store/auth';
import { ensureSubscribed, unsubscribe, isSubscribed } from '../sync/push';
import { toast } from '../store/toast';
import type { DeviceRow } from '../api/types';

function maskIp(ip: string | null): string {
  if (!ip) return '—';
  // IPv4: 192.168.1.10 -> 192.168.*.* (前两段)
  // IPv6: 完整显示
  if (ip.includes(':')) return ip;
  const parts = ip.split('.');
  if (parts.length === 4) return `${parts[0]}.${parts[1]}.*.*`;
  return ip;
}

function platformIcon(platform: string | null): string {
  switch (platform) {
    case 'ios':
      return '📱';
    case 'android':
      return '🤖';
    case 'macos':
      return '💻';
    case 'windows':
      return '🪟';
    case 'linux':
      return '🐧';
    default:
      return '🖥️';
  }
}

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return '刚刚';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return new Date(ts).toLocaleDateString('zh-CN');
}

export function Settings() {
  const clear = useAuthStore((s) => s.clear);
  const myDeviceId = useAuthStore((s) => s.deviceId);
  const myDeviceName = useAuthStore((s) => s.deviceName);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // 当前正在操作的 device id
  const [pushOn, setPushOn] = useState<boolean | null>(null);
  const [pushBusy, setPushBusy] = useState(false);

  const refresh = async () => {
    try {
      const list = await devicesApi.list();
      setDevices(list);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // 检测 push 订阅状态
  useEffect(() => {
    isSubscribed().then(setPushOn);
  }, []);

  const handleRename = async (d: DeviceRow) => {
    const name = prompt(`给设备改个名字：`, d.name);
    if (!name || name === d.name) return;
    setBusy(d.id);
    try {
      await devicesApi.rename(d.id, name);
      await refresh();
    } catch (err) {
      alert(`改名失败: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleBlock = async (d: DeviceRow) => {
    if (d.id === myDeviceId) {
      alert('不能拉黑当前设备。请先在另一台设备上拉黑。');
      return;
    }
    if (!confirm(`拉黑设备 "${d.name}"？该设备会被立即踢下线并禁止再登录。`)) return;
    const reason = prompt('拉黑原因（可留空）：') ?? undefined;
    setBusy(d.id);
    try {
      await devicesApi.block(d.id, reason);
      await refresh();
    } catch (err) {
      alert(`拉黑失败: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleUnblock = async (d: DeviceRow) => {
    setBusy(d.id);
    try {
      await devicesApi.unblock(d.id);
      await refresh();
    } catch (err) {
      alert(`解封失败: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (d: DeviceRow) => {
    if (d.id === myDeviceId) {
      alert('不能删除当前设备。请先登出。');
      return;
    }
    if (!confirm(`彻底删除设备 "${d.name}"？该设备如要再用必须重新输密码。`)) return;
    setBusy(d.id);
    try {
      await devicesApi.remove(d.id);
      await refresh();
    } catch (err) {
      alert(`删除失败: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleTogglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        const ok = await unsubscribe();
        if (ok) {
          setPushOn(false);
          toast({ kind: 'info', text: '已关闭提醒通知' });
        } else {
          toast({ kind: 'error', text: '取消订阅失败' });
        }
      } else {
        const result = await ensureSubscribed();
        if (result === 'subscribed') {
          setPushOn(true);
          toast({ kind: 'success', text: '提醒通知已开启 — 到点会推送' });
        } else if (result === 'denied') {
          toast({ kind: 'warn', text: '浏览器已禁止通知, 请在浏览器设置中允许' });
        } else if (result === 'unsupported') {
          toast({ kind: 'warn', text: '当前浏览器不支持推送' });
        } else {
          toast({ kind: 'error', text: '开启失败, 请重试' });
        }
      }
    } finally {
      setPushBusy(false);
    }
  };

  // 触发一次系统通知 (验证通道)
  const handleTestNotification = async () => {
    if (!('Notification' in window)) {
      toast({ kind: 'error', text: '浏览器不支持通知' });
      return;
    }
    if (Notification.permission === 'denied') {
      toast({ kind: 'error', text: '通知已被浏览器禁止, 请在浏览器设置中允许' });
      return;
    }
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast({ kind: 'warn', text: '需要先允许通知' });
        return;
      }
    }
    // 用 SW 注册的 showNotification, 这样走 push 通道
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (reg) {
        await reg.showNotification('Todo 测试通知', {
          body: '看到这个说明系统通知通道正常 ✓ 到点提醒会发到这',
          icon: '/icon-192.svg',
          badge: '/icon-192.svg',
          tag: 'todo-test',
        });
        toast({ kind: 'success', text: '已发送系统通知, 看看系统通知中心' });
        return;
      }
    }
    // fallback: 直接 new Notification
    new Notification('Todo 测试通知', {
      body: '看到这个说明系统通知通道正常 ✓',
    });
    toast({ kind: 'success', text: '已发送系统通知' });
  };

  return (
    <div className="mx-auto max-w-3xl p-6 safe-top">
      {/* 顶部 */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">设置</h1>
          <p className="text-xs text-gray-400 mt-1">管理登录过的设备</p>
        </div>
        <div className="flex gap-2 text-sm">
          <Link
            to="/"
            className="rounded border border-gray-300 px-3 py-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            ← 返回
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20">
          {error}
        </div>
      )}

      {/* 当前设备信息 */}
      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
        <h2 className="text-sm font-medium text-blue-900 dark:text-blue-300">
          当前设备
        </h2>
        <p className="mt-1 text-sm text-blue-700 dark:text-blue-200">
          {platformIcon(null)} <strong>{myDeviceName ?? '未知'}</strong>
        </p>
        <button
          onClick={clear}
          className="mt-3 text-xs text-blue-700 underline hover:text-blue-900 dark:text-blue-300"
        >
          退出登录
        </button>
      </div>

      {/* 推送通知 */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        {/* 第一行: 标题 + 开启/关闭按钮 */}
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-medium">提醒通知</h2>
          <button
            onClick={handleTogglePush}
            disabled={pushBusy || pushOn === null}
            className={`shrink-0 rounded-lg px-4 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
              pushOn
                ? 'border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {pushBusy ? '处理中…' : pushOn ? '关闭' : '开启'}
          </button>
        </div>
        {/* 第二行: 描述 + 状态 */}
        <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          开启后, 任务的提醒时间到了会推送到本设备, 点击通知可打开任务。
        </p>
        <p className="mt-1 text-xs">
          <span className="text-gray-400">状态: </span>
          {pushOn === null ? (
            <span className="text-gray-400">检测中…</span>
          ) : pushOn ? (
            <span className="text-green-600 dark:text-green-400">已开启 ✓</span>
          ) : (
            <span className="text-gray-400">未开启</span>
          )}
        </p>
        {/* 第三行: 测试按钮 (弱化) */}
        <button
          onClick={handleTestNotification}
          className="mt-3 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          测试通知
        </button>
      </div>

      {/* 设备列表 */}
      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h2 className="font-medium">所有设备 ({devices.length})</h2>
          <button
            onClick={refresh}
            className="text-xs text-gray-400 hover:text-gray-600"
            title="刷新"
          >
            ↻ 刷新
          </button>
        </div>

        {devices.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-400">没有设备记录</p>
        ) : (
          <ul>
            {devices.map((d, i) => {
              const isMe = d.id === myDeviceId;
              const isBlocked = d.status === 'blocked';
              return (
                <li
                  key={d.id}
                  className={`p-4 ${
                    i < devices.length - 1
                      ? 'border-b border-gray-100 dark:border-gray-700'
                      : ''
                  } ${isBlocked ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* 左侧信息 */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{platformIcon(d.platform)}</span>
                        <span className="font-medium">{d.name}</span>
                        {isMe && (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            当前
                          </span>
                        )}
                        {isBlocked && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            已拉黑
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-400 space-y-0.5">
                        <div>
                          平台: {d.platform ?? '未知'} · IP: {maskIp(d.ip_last_seen)}
                        </div>
                        <div>
                          首次登录: {formatRelativeTime(d.first_seen_at)} ·{' '}
                          最后活跃: {formatRelativeTime(d.last_seen_at)}
                        </div>
                        {d.blocked_reason && (
                          <div className="text-red-500">拉黑原因: {d.blocked_reason}</div>
                        )}
                      </div>
                    </div>

                    {/* 右侧操作 */}
                    <div className="flex shrink-0 flex-col gap-1 text-xs">
                      <button
                        onClick={() => handleRename(d)}
                        disabled={busy === d.id}
                        className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        改名
                      </button>
                      {isBlocked ? (
                        <button
                          onClick={() => handleUnblock(d)}
                          disabled={busy === d.id}
                          className="rounded px-2 py-1 text-green-600 hover:bg-green-50 disabled:opacity-50 dark:hover:bg-green-900/20"
                        >
                          解封
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBlock(d)}
                          disabled={busy === d.id || isMe}
                          className="rounded px-2 py-1 text-orange-600 hover:bg-orange-50 disabled:opacity-50 dark:hover:bg-orange-900/20"
                        >
                          拉黑
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(d)}
                        disabled={busy === d.id || isMe}
                        className="rounded px-2 py-1 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
                      >
                        删除
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 安全提示 */}
      <div className="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
        <strong>安全提示：</strong>
        <ul className="mt-1 list-disc pl-5 space-y-0.5">
          <li>不认识的设备 → 立刻拉黑，会被秒踢下线</li>
          <li>拉黑后该设备如要再用，必须重新输密码</li>
          <li>删除设备记录后，该设备下次访问会被当成"新设备"，需要重新输密码</li>
        </ul>
      </div>
    </div>
  );
}